import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/lib/api';
import { translateError, handleUnauthorizedError } from '@/lib/errorTranslation';
import { LanguageToggle } from '@/components/LanguageToggle';
import { ArrowLeft, Plus, Pencil, Trash2, Syringe, Upload, X, Image as ImageIcon } from 'lucide-react';

interface Vaccine {
  id: number;
  label: string;
  total_doses: number;
  image_url?: string;
  created_at: string;
  schedule_count?: number;
}

interface VaccineFormData {
  label: string;
  total_doses: number;
}

export function DoctorVaccines() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [vaccines, setVaccines] = useState<Vaccine[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingVaccine, setEditingVaccine] = useState<Vaccine | null>(null);
  const [deletingVaccine, setDeletingVaccine] = useState<Vaccine | null>(null);
  const [formData, setFormData] = useState<VaccineFormData>({
    label: '',
    total_doses: 1,
  });
  const [saving, setSaving] = useState(false);
  
  // Image upload state
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    loadVaccines();
  }, []);

  const loadVaccines = async () => {
    try {
      const result: any = await api.getVaccines();
      setVaccines(result.vaccines || []);
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({
        title: t('app.error'),
        description: translateError(error, t),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingVaccine(null);
    setFormData({
      label: '',
      total_doses: 1,
    });
    setSelectedImage(null);
    setImagePreview(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (vaccine: Vaccine) => {
    setEditingVaccine(vaccine);
    setFormData({
      label: vaccine.label,
      total_doses: vaccine.total_doses,
    });
    setSelectedImage(null);
    setImagePreview(vaccine.image_url || null);
    setIsDialogOpen(true);
  };

  const openDeleteDialog = (vaccine: Vaccine) => {
    setDeletingVaccine(vaccine);
    setIsDeleteDialogOpen(true);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: t('app.error'),
          description: 'Invalid file type. Allowed: JPEG, PNG, GIF, WebP',
          variant: 'destructive',
        });
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: t('app.error'),
          description: 'File too large. Maximum size: 5MB',
          variant: 'destructive',
        });
        return;
      }
      
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload: any = {
        label: formData.label,
        total_doses: formData.total_doses,
      };

      let vaccineId: number;

      if (editingVaccine) {
        await api.updateVaccine(editingVaccine.id, payload);
        vaccineId = editingVaccine.id;
        toast({
          title: t('app.success'),
          description: t('vaccine.editVaccine'),
        });
      } else {
        const result: any = await api.createVaccine(payload);
        vaccineId = result.vaccine?.id;
        toast({
          title: t('app.success'),
          description: t('vaccine.addVaccine'),
        });
      }

      // Upload image if selected
      if (selectedImage && vaccineId) {
        setUploadingImage(true);
        try {
          await api.uploadVaccineImage(vaccineId, selectedImage);
          toast({
            title: t('app.success'),
            description: 'Image uploaded successfully',
          });
        } catch (imgError: any) {
          toast({
            title: t('app.error'),
            description: 'Vaccine saved but image upload failed: ' + imgError.message,
            variant: 'destructive',
          });
        } finally {
          setUploadingImage(false);
        }
      }

      setIsDialogOpen(false);
      loadVaccines();
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      toast({
        title: t('app.error'),
        description: translateError(error, t),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingVaccine) return;

    try {
      await api.deleteVaccine(deletingVaccine.id);
      toast({
        title: t('app.success'),
        description: t('vaccine.deleteVaccine'),
      });
      setIsDeleteDialogOpen(false);
      setDeletingVaccine(null);
      loadVaccines();
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'doctor')) return;
      const errorMsg = error.message || String(error);
      toast({
        title: t('app.error'),
        description: errorMsg.includes('in use') 
          ? t('vaccine.vaccineInUse') 
          : translateError(error, t),
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t('app.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">💉 {t('nav.vaccines')}</h1>
          </div>
          <div className="flex gap-2">
            <LanguageToggle />
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              {t('vaccine.addVaccine')}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Syringe className="h-5 w-5" />
              {t('vaccine.vaccines')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {vaccines.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Syringe className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t('vaccine.noVaccines')}</p>
                <Button className="mt-4" onClick={openCreateDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('vaccine.addVaccine')}
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">{t('vaccine.image')}</TableHead>
                    <TableHead>{t('vaccine.vaccineName')}</TableHead>
                    <TableHead>{t('vaccine.totalDoses')}</TableHead>
                    <TableHead>{t('vaccine.schedules')}</TableHead>
                    <TableHead className="text-right">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vaccines.map((vaccine) => (
                    <TableRow key={vaccine.id}>
                      <TableCell>
                        {vaccine.image_url ? (
                          <img
                            src={vaccine.image_url}
                            alt={vaccine.label}
                            className="h-10 w-10 object-cover rounded"
                          />
                        ) : (
                          <div className="h-10 w-10 bg-gray-100 rounded flex items-center justify-center">
                            <ImageIcon className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{vaccine.label}</TableCell>
                      <TableCell>{vaccine.total_doses}</TableCell>
                      <TableCell>{vaccine.schedule_count || 0} {t('vaccine.configured')}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(vaccine)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(vaccine)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingVaccine ? t('vaccine.editVaccine') : t('vaccine.addVaccine')}
            </DialogTitle>
            <DialogDescription>
              {editingVaccine 
                ? 'Update the vaccine details below.' 
                : 'Fill in the vaccine details below.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="label">{t('vaccine.vaccineName')} *</Label>
                <Input
                  id="label"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="total_doses">{t('vaccine.totalDoses')} *</Label>
                <Input
                  id="total_doses"
                  type="number"
                  min="1"
                  value={formData.total_doses}
                  onChange={(e) => setFormData({ ...formData, total_doses: Number.parseInt(e.target.value, 10) })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">{t('vaccine.schedulesNote')}</p>
              </div>
              
              {/* Image Upload */}
              <div>
                <Label>{t('vaccine.image')}</Label>
                <div className="mt-2">
                  {imagePreview ? (
                    <div className="relative inline-block">
                      <img
                        src={imagePreview}
                        alt="Vaccine preview"
                        className="h-32 w-32 object-cover rounded-lg border"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6"
                        onClick={clearImage}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center h-32 w-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="h-8 w-8 text-gray-400 mb-2" />
                        <p className="text-xs text-gray-500">Upload</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleImageSelect}
                      />
                    </label>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Max 5MB. JPEG, PNG, GIF, WebP</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={saving || uploadingImage}>
                {saving || uploadingImage ? t('app.loading') : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('vaccine.deleteVaccine')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('vaccine.confirmDelete')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
